/**
 * Formulario de alta de negocio (T025 · FR-001).
 * Valida contra el backend y pasa a la previsualización — no emite aquí.
 */
import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'

import { useSesion } from '../../auth'
import {
    diasAbiertos,
    horarioPorDefecto,
    HorarioSemana,
    sobreDeHoras,
    type HorarioSemanal,
} from './horario'
import { PuestaEnMarcha } from './puesta-en-marcha'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import { Textarea } from '../../components/ui/textarea'
import {
    borradorPorDefecto,
    guardarBorrador,
    guardarPrevisualizacion,
    importarAltaApi,
    leerBorrador,
    type EntradaAlta,
    type ErrorCampo,
    validarAltaApi,
} from '../../lib/altas'
import { usarBorrador } from './usar-borrador'
import { SelectorLineaLibre } from './selector-linea-whatsapp'

const DIAS = [
    { v: 0, et: 'Dom' },
    { v: 1, et: 'Lun' },
    { v: 2, et: 'Mar' },
    { v: 3, et: 'Mié' },
    { v: 4, et: 'Jue' },
    { v: 5, et: 'Vie' },
    { v: 6, et: 'Sáb' },
]

function Campo({
    id,
    label,
    children,
}: {
    id: string
    label: string
    children: React.ReactNode
}) {
    return (
        <div className="space-y-1.5">
            <Label htmlFor={id}>{label}</Label>
            {children}
        </div>
    )
}

function Seccion({ titulo, children }: { titulo: string; children: React.ReactNode }) {
    return (
        <section className="space-y-3 border-b border-neutral-800 pb-6">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-neutral-400">{titulo}</h2>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">{children}</div>
        </section>
    )
}

export function AltaNueva() {
    const { data: sesion } = useSesion()
    const navigate = useNavigate()
    const [entrada, setEntrada] = useState<EntradaAlta>(() => leerBorrador() ?? borradorPorDefecto())
    const [errores, setErrores] = useState<ErrorCampo[]>([])
    const [enviando, setEnviando] = useState(false)
    const [conWhatsapp, setConWhatsapp] = useState(Boolean(entrada.whatsapp))
    // El detalle por día vive aquí; al contrato viejo solo va el sobre de horas.
    const [horario, setHorario] = useState<HorarioSemanal>(() => horarioPorDefecto())

    // ── El borrador que sobrevive a cerrar la pestaña (US5) ───────────────────
    const [params] = useSearchParams()
    const idBorrador = Number(params.get('borrador')) || null
    const borrador = usarBorrador(
        idBorrador,
        (datos) => {
            setEntrada(datos)
            setConWhatsapp(Boolean(datos.whatsapp))
        },
        (id) => navigate(`/altas/nueva?borrador=${id}`, { replace: true }),
    )

    if (sesion && sesion.role !== 'admin') {
        return <p className="text-sm text-neutral-500">No encontrado</p>
    }

    function patch(p: Partial<EntradaAlta>) {
        setEntrada((e) => ({ ...e, ...p }))
    }

    /** Guardar a medias. No valida: ver la cabecera de `usar-borrador.ts`. */
    function guardarParaLuego() {
        return borrador.guardar(entrada, entrada.clinicName.trim() || 'Alta sin nombre')
    }

    async function previsualizar(e: React.FormEvent) {
        e.preventDefault()
        setErrores([])
        setEnviando(true)
        try {
            const {
                email,
                website,
                address,
                phone,
                parking,
                transport,
                extraInfo,
                whatsapp: _wa,
                ...base
            } = entrada
            const payload: EntradaAlta = { ...base }

            // ── LO QUE ANTES SE PEDÍA DOS VECES ──────────────────────────
            //
            // El catálogo y las reglas de reserva vivían en dos sitios: en estos
            // campos, del contrato viejo, y en la ficha. Pedirlos dos veces no
            // era solo incómodo: era dos verdades que podían discrepar, y el
            // aprovisionador ya toma la de la ficha. Ahora se derivan de ella.
            //
            // `slotStepMinutes` desaparece del todo: la agenda 1.6.0 lo IGNORA
            // en silencio, así que preguntarlo era pedirle a alguien que
            // decidiera algo que no tiene efecto (FR-019).
            const f = entrada.ficha
            payload.services = f.catalogo.servicios.map((s) => ({
                name: s.nombre,
                // La agenda bloquea la SUMA; al cliente se le dice la duración.
                duration: s.duracionMin + s.margenMin,
                ...(s.precio !== undefined ? { price: s.precio } : {}),
            }))
            payload.providers = f.catalogo.profesionales.map((p) => ({
                name: p.nombre,
                serviceIndexes: p.serviciosIndices,
            }))
            payload.horarioSemanal = Object.fromEntries(
                Object.entries(horario).map(([k, v]) => [k, { ...v }]),
            )
            payload.maxHorizonDays = f.reglas.horizonteMaximoDias
            payload.maxActiveBookings = f.reglas.maxCitasActivasPorCliente
            payload.cancelWindowHours = f.reglas.antelacionCancelacionHoras
            if (email) payload.email = email
            if (website) payload.website = website
            if (address) payload.address = address
            if (phone) payload.phone = phone
            if (parking) payload.parking = parking
            if (transport) payload.transport = transport
            if (extraInfo) payload.extraInfo = extraInfo
            if (entrada.ficha.nAdministradores !== undefined) {
                payload.nAdministradores = entrada.ficha.nAdministradores
            }
            if (conWhatsapp && entrada.whatsapp) payload.whatsapp = entrada.whatsapp

            const r = await validarAltaApi(payload)
            if (!r.valido) {
                setErrores(r.errores)
                return
            }
            guardarBorrador(payload)
            guardarPrevisualizacion(r.previsualizacion)
            navigate('/altas/previsualizar')
        } catch {
            setErrores([{ campo: '(raiz)', motivo: 'formato_invalido' }])
        } finally {
            setEnviando(false)
        }
    }

    async function alImportar(file: File | null) {
        if (!file) return
        setErrores([])
        const texto = await file.text()
        const r = await importarAltaApi(texto)
        if (!r.valido) {
            setErrores(r.errores)
            return
        }
        // El import solo valida; rellenamos el borrador parseando el JSON.
        try {
            const parsed = JSON.parse(texto) as EntradaAlta
            setEntrada(parsed)
            setConWhatsapp(Boolean(parsed.whatsapp))
            guardarBorrador(parsed)
            guardarPrevisualizacion(r.previsualizacion)
            navigate('/altas/previsualizar')
        } catch {
            setErrores([{ campo: '(raiz)', motivo: 'formato_invalido' }])
        }
    }

    return (
        <div className="mx-auto w-full max-w-[1700px] space-y-6" data-testid="alta-formulario">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <Link to="/" className="text-xs text-neutral-500 hover:text-neutral-300">
                        ← Operación
                    </Link>
                    <h1 className="mt-1 text-lg font-semibold text-neutral-100">Nueva alta</h1>
                    <p className="text-xs text-neutral-500">
                        Se valida y previsualiza. Nada se crea hasta que confirmes.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Link to="/altas/borradores" className="text-xs text-neutral-500 hover:text-neutral-300">
                        Altas a medias
                    </Link>
                    {/* US5 · guardar SIN validar: ver `guardarParaLuego`. */}
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={borrador.guardando}
                        onClick={() => void guardarParaLuego()}
                    >
                        {borrador.guardando ? 'Guardando…' : 'Guardar y seguir luego'}
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => document.getElementById('alta-importar')?.click()}
                    >
                        Importar JSON
                    </Button>
                    <input
                        id="alta-importar"
                        type="file"
                        accept="application/json,.json"
                        className="hidden"
                        onChange={(ev) => void alImportar(ev.target.files?.[0] ?? null)}
                    />
                </div>
            </div>

            {borrador.aviso && (
                <p className="rounded-md border border-amber-900/50 bg-amber-950/30 px-3 py-2 text-xs text-amber-200">
                    {borrador.aviso}
                </p>
            )}

            <form onSubmit={(e) => void previsualizar(e)} className="space-y-6">
                <PuestaEnMarcha
                    ficha={entrada.ficha}
                    onChange={(ficha) => patch({ ficha })}
                    nombreNegocio={entrada.clinicName}
                    extras={{
                        identidad: (
                            <>
                <Seccion titulo="Datos de aprovisionamiento">
                    <Campo id="clinicName" label="Nombre del negocio">
                        <Input
                            id="clinicName"
                            value={entrada.clinicName}
                            onChange={(e) => patch({ clinicName: e.target.value })}
                            required
                        />
                    </Campo>
                    <Campo id="eaCalendarId" label="Slug de agenda (kebab-case)">
                        <Input
                            id="eaCalendarId"
                            value={entrada.eaCalendarId}
                            onChange={(e) => patch({ eaCalendarId: e.target.value })}
                            placeholder="clinica-ejemplo"
                            required
                        />
                    </Campo>
                </Seccion>
                        </>
                        ),
                        reglas: (
                            <>
                <Seccion titulo="Horario de la agenda">
                    <div className="md:col-span-2 xl:col-span-3">
                        <p className="mb-3 text-xs text-neutral-500">
                            Cuándo se puede reservar de verdad. Va a la agenda, no a la ficha: es de
                            donde el asistente lee los huecos.
                        </p>
                        <HorarioSemana
                            horario={horario}
                            onChange={(h) => {
                                setHorario(h)
                                const sobre = sobreDeHoras(h)
                                patch({
                                    openDays: diasAbiertos(h),
                                    openHour: sobre.abre,
                                    closeHour: sobre.cierra,
                                    // El detalle por día, que es lo que de verdad
                                    // se siembra en la agenda.
                                    horarioSemanal: Object.fromEntries(
                                        Object.entries(h).map(([k, v]) => [k, { ...v }]),
                                    ),
                                })
                            }}
                        />
                    </div>
                </Seccion>
                        </>
                        ),
                        contacto: (
                            <>
                <Seccion titulo="Contacto">
                    <Campo id="address" label="Dirección">
                        <Input
                            id="address"
                            value={entrada.address ?? ''}
                            onChange={(e) => patch({ address: e.target.value })}
                        />
                    </Campo>
                    <Campo id="phone" label="Teléfono">
                        <Input
                            id="phone"
                            value={entrada.phone ?? ''}
                            onChange={(e) => patch({ phone: e.target.value })}
                        />
                    </Campo>
                    <Campo id="email" label="Correo">
                        <Input
                            id="email"
                            type="email"
                            value={entrada.email ?? ''}
                            onChange={(e) => patch({ email: e.target.value })}
                        />
                    </Campo>
                    <Campo id="website" label="Web">
                        <Input
                            id="website"
                            type="url"
                            value={entrada.website ?? ''}
                            onChange={(e) => patch({ website: e.target.value })}
                        />
                    </Campo>
                </Seccion>
                        </>
                        ),
                        whatsapp: (
                            <>
                <Seccion titulo="WhatsApp (opcional)">
                    <div className="md:col-span-2">
                        <label className="flex items-center gap-2 text-xs text-neutral-400">
                            <input
                                type="checkbox"
                                checked={conWhatsapp}
                                onChange={(e) => {
                                    setConWhatsapp(e.target.checked)
                                    if (e.target.checked && !entrada.whatsapp) {
                                        patch({
                                            whatsapp: {
                                                phoneNumber: '',
                                                apiKey: '',
                                                phoneNumberId: '',
                                                businessAccountId: '',
                                            },
                                        })
                                    }
                                }}
                            />
                            Incluir credenciales de Meta ahora
                        </label>
                    </div>
                    {conWhatsapp && entrada.whatsapp && (
                        <>
                            <SelectorLineaLibre
                                onElegir={(l) =>
                                    patch({
                                        whatsapp: {
                                            phoneNumber: l.phoneNumber,
                                            phoneNumberId: l.phoneNumberId,
                                            businessAccountId: l.businessAccountId,
                                            apiKey: l.apiKey ?? entrada.whatsapp?.apiKey ?? '',
                                        },
                                    })
                                }
                            />
                            <Campo id="wa-phone" label="Número">
                                <Input
                                    id="wa-phone"
                                    value={entrada.whatsapp.phoneNumber}
                                    onChange={(e) =>
                                        patch({
                                            whatsapp: { ...entrada.whatsapp!, phoneNumber: e.target.value },
                                        })
                                    }
                                />
                            </Campo>
                            <Campo id="wa-key" label="API key">
                                <Input
                                    id="wa-key"
                                    type="password"
                                    autoComplete="off"
                                    value={entrada.whatsapp.apiKey}
                                    onChange={(e) =>
                                        patch({
                                            whatsapp: { ...entrada.whatsapp!, apiKey: e.target.value },
                                        })
                                    }
                                />
                            </Campo>
                            <Campo id="wa-pnid" label="Phone number ID">
                                <Input
                                    id="wa-pnid"
                                    value={entrada.whatsapp.phoneNumberId}
                                    onChange={(e) =>
                                        patch({
                                            whatsapp: {
                                                ...entrada.whatsapp!,
                                                phoneNumberId: e.target.value,
                                            },
                                        })
                                    }
                                />
                            </Campo>
                            <Campo id="wa-baid" label="Business account ID">
                                <Input
                                    id="wa-baid"
                                    value={entrada.whatsapp.businessAccountId}
                                    onChange={(e) =>
                                        patch({
                                            whatsapp: {
                                                ...entrada.whatsapp!,
                                                businessAccountId: e.target.value,
                                            },
                                        })
                                    }
                                />
                            </Campo>
                        </>
                    )}
                </Seccion>
                        </>
                        ),
                    }}
                />





                {errores.length > 0 && (
                    <ul
                        role="alert"
                        data-testid="alta-errores"
                        className="space-y-1 rounded-md border border-red-900/60 bg-red-950/40 p-3 text-xs text-red-300"
                    >
                        {errores.map((err) => (
                            <li key={`${err.campo}:${err.motivo}`}>
                                <span className="text-neutral-400">{err.campo}</span> —{' '}
                                {/* El texto va delante del código: el `motivo` sirve para
                                    que el programa decida, pero un «inconsistente» a secas
                                    no le dice a nadie qué corregir. Y cuando el problema
                                    viene del asistente, llega ya redactado: resumirlo sería
                                    volver a tirar lo que costó un alta recuperar. */}
                                {err.mensaje ?? err.motivo}
                            </li>
                        ))}
                    </ul>
                )}

                <div className="flex justify-end gap-3">
                    <Button type="submit" disabled={enviando} data-testid="alta-previsualizar">
                        {enviando ? 'Validando…' : 'Previsualizar'}
                    </Button>
                </div>
            </form>
        </div>
    )
}
